/**
 * DocumentScanner — Kamera-basierter Dokumenten-Scanner
 * 
 * Mobile-First: Nutzt die HTML5 Camera API für Smartphone-Scan.
 * Desktop: Datei-Upload als Fallback.
 * 
 * Features:
 * - Kamera-Aufnahme (Rückkamera bevorzugt)
 * - Datei-Upload (Drag & Drop + Button)
 * - Mehrseitige Dokumente
 * - Vorschau vor dem Senden
 * - Automatische KI-Analyse nach Aufnahme
 */
import { useState, useRef, useCallback } from 'react';
import { Camera, Upload, X, RotateCcw, Check, Loader2, FileText, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

interface ScanResult {
  image: string; // base64
  mimeType: string;
  thumbnail: string; // data URL für Vorschau
}

interface DocumentScannerProps {
  onScanComplete: (scans: ScanResult[]) => void;
  onCancel: () => void;
  isAnalyzing?: boolean;
}

export default function DocumentScanner({ onScanComplete, onCancel, isAnalyzing }: DocumentScannerProps) {
  const [scans, setScans] = useState<ScanResult[]>([]);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ─── Kamera starten ───────────────────────────────────────────────────────
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' }, // Rückkamera
          width: { ideal: 2048 },
          height: { ideal: 2048 },
        },
      });
      setCameraStream(stream);
      setShowCamera(true);
      // Video-Element verbinden
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 100);
    } catch (err: any) {
      console.error('Kamera-Fehler:', err);
      toast.error('Kamera nicht verfügbar. Bitte Datei-Upload nutzen.');
      // Fallback: Datei-Upload öffnen
      fileInputRef.current?.click();
    }
  }, []);

  // ─── Kamera stoppen ───────────────────────────────────────────────────────
  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  }, [cameraStream]);

  // ─── Foto aufnehmen ──────────────────────────────────────────────────────
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0);

    // Als JPEG mit hoher Qualität
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const base64 = dataUrl.split(',')[1];

    setScans(prev => [...prev, {
      image: base64,
      mimeType: 'image/jpeg',
      thumbnail: dataUrl,
    }]);

    toast.success('Seite aufgenommen');
    stopCamera();
  }, [stopCamera]);

  // ─── Datei-Upload ─────────────────────────────────────────────────────────
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        const base64 = dataUrl.split(',')[1];
        setScans(prev => [...prev, {
          image: base64,
          mimeType: file.type || 'image/jpeg',
          thumbnail: dataUrl,
        }]);
        toast.success(`${file.name} hinzugefügt`);
      };
      reader.readAsDataURL(file);
    });

    // Input zurücksetzen
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // ─── Scan entfernen ───────────────────────────────────────────────────────
  const removeScan = useCallback((index: number) => {
    setScans(prev => prev.filter((_, i) => i !== index));
  }, []);

  // ─── Kamera-Ansicht ──────────────────────────────────────────────────────
  if (showCamera) {
    return (
      <div className="fixed inset-0 z-50 bg-black flex flex-col">
        {/* Video-Feed */}
        <div className="flex-1 relative">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
          {/* Rahmen-Overlay */}
          <div className="absolute inset-8 border-2 border-white/40 rounded-lg pointer-events-none" />
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-4 py-2 rounded-full">
            Dokument im Rahmen positionieren
          </div>
        </div>

        {/* Steuerung */}
        <div className="bg-black/90 px-6 py-6 flex items-center justify-between">
          <button
            onClick={() => { stopCamera(); }}
            className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          <button
            onClick={capturePhoto}
            className="w-18 h-18 rounded-full bg-white border-4 border-white/40 flex items-center justify-center active:scale-95 transition-transform"
            style={{ width: 72, height: 72 }}
          >
            <div className="w-14 h-14 rounded-full bg-white" style={{ width: 56, height: 56 }} />
          </button>

          <button
            onClick={() => { stopCamera(); fileInputRef.current?.click(); }}
            className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center"
          >
            <Upload className="w-6 h-6 text-white" />
          </button>
        </div>

        <canvas ref={canvasRef} className="hidden" />
      </div>
    );
  }

  // ─── Hauptansicht (Scan-Übersicht) ────────────────────────────────────────
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
        <button onClick={onCancel} className="text-gray-500 hover:text-gray-700 p-1">
          <X className="w-5 h-5" />
        </button>
        <h2 className="text-base font-semibold text-gray-900">Dokument scannen</h2>
        <div className="w-7" />
      </div>

      {/* Inhalt */}
      <div className="flex-1 overflow-auto p-4">
        {scans.length === 0 ? (
          /* Leerer Zustand — Scan starten */
          <div className="flex flex-col items-center justify-center h-full gap-6 py-12">
            <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center">
              <FileText className="w-10 h-10 text-gray-400" />
            </div>
            <div className="text-center">
              <p className="text-base font-medium text-gray-900">Dokument digitalisieren</p>
              <p className="text-sm text-gray-500 mt-1">Fotografieren oder Datei hochladen</p>
            </div>

            <div className="flex flex-col gap-3 w-full max-w-xs">
              <button
                onClick={startCamera}
                className="flex items-center justify-center gap-2 px-6 py-3.5 bg-[#0D9488] text-white rounded-xl font-medium hover:bg-[#0B8278] active:scale-[0.98] transition-all"
              >
                <Camera className="w-5 h-5" />
                Kamera öffnen
              </button>

              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center justify-center gap-2 px-6 py-3.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 active:scale-[0.98] transition-all"
              >
                <Upload className="w-5 h-5" />
                Datei auswählen
              </button>
            </div>
          </div>
        ) : (
          /* Scans vorhanden — Vorschau */
          <div className="space-y-4">
            {/* Thumbnails */}
            <div className="grid grid-cols-2 gap-3">
              {scans.map((scan, i) => (
                <div key={i} className="relative group rounded-xl overflow-hidden border border-gray-200 bg-gray-50 aspect-[3/4]">
                  <img
                    src={scan.thumbnail}
                    alt={`Seite ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
                    {i + 1}
                  </div>
                  <button
                    onClick={() => removeScan(i)}
                    className="absolute top-2 right-2 w-7 h-7 bg-red-500/90 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}

              {/* Weitere Seite hinzufügen */}
              <button
                onClick={startCamera}
                className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 aspect-[3/4] flex flex-col items-center justify-center gap-2 hover:border-[#0D9488] hover:bg-[#0D9488]/5 transition-colors"
              >
                <Plus className="w-6 h-6 text-gray-400" />
                <span className="text-xs text-gray-500">Seite hinzufügen</span>
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              {scans.length} {scans.length === 1 ? 'Seite' : 'Seiten'} gescannt
            </p>
          </div>
        )}
      </div>

      {/* Footer — Analyse starten (pb-24 für Mobile-Bottom-Navigation) */}
      {scans.length > 0 && (
        <div className="px-4 pt-4 pb-24 border-t border-gray-200 bg-white" style={{ paddingBottom: 'max(6rem, calc(1.5rem + env(safe-area-inset-bottom)))' }}>
          <button
            onClick={() => onScanComplete(scans)}
            disabled={isAnalyzing}
            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-[#0D9488] text-white rounded-xl font-medium hover:bg-[#0B8278] active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isAnalyzing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                KI analysiert...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Analysieren ({scans.length} {scans.length === 1 ? 'Seite' : 'Seiten'})
              </>
            )}
          </button>
        </div>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        capture="environment"
        onChange={handleFileUpload}
        className="hidden"
      />
    </div>
  );
}
